import { useEffect, useMemo, useRef, useState } from 'react'
import { NavLink, Outlet, useParams, Link, useLocation, Navigate } from 'react-router-dom'
import ScaleTag from './ScaleTag.jsx'
import {
  User,
  CalendarDays,
  Stethoscope,
  ClipboardList,
  FlaskConical,
  BadgePercent,
  Pin,
  ScrollText,
  Phone,
  Mail,
  AlertTriangle,
  Droplet,
  HeartPulse,
  Cake,
  ArrowLeft,
  LogOut,
  PanelLeftClose,
  PanelLeftOpen,
  Menu,
  X,
} from 'lucide-react'
import { apiGet } from '../lib/api.js'
import { useMe, useDisplayName } from '../lib/me.js'
import { nameInitial } from '../lib/text.js'
import { isDoctorOnly } from '../lib/role.js'
import { clearAllRecentPatients } from '../lib/recentPatients.js'
import { popupNotes } from '../lib/patientNotes.js'
import { useXanoRealtime } from '../lib/realtime.js'
import PatientNoteAlert from './patient/PatientNoteAlert.jsx'
import PatientChatDock from './patient/PatientChatDock.jsx'
import PatientChatButton from './patient/PatientChatButton.jsx'
import ChatToaster from './ChatToaster.jsx'
import ChatDockHost from './ChatDockHost.jsx'
import { onOpenChatDock, markChatDockOpen } from '../lib/chatDock.js'
import BrandMark from './BrandMark.jsx'
import SearchTrigger from './SearchTrigger.jsx'
import StatusControl from './StatusControl.jsx'

/* The patient sidebar, trimmed 2026-08-16: โปรไฟล์ and Old version are gone and
   โปรโมชั่น moved up beside Chart, where the clinical run of the menu now reads
   ข้อมูล → นัด → รักษา → Chart → โปรโมชั่น → Lab → ติดตาม → Log.
   ⚠️ Only the MENU entries were dropped — `/patient/:id/profile` and
   `/patient/:id/old-version` are still routed in App.jsx, so a bookmark or an
   old link still resolves instead of 404-ing. Delete the routes and their pages
   too when it is clear nobody wants them back. */
const NAV = [
  { to: 'info',         label: 'ข้อมูลผู้ป่วย', icon: User },
  { to: 'appointments', label: 'นัดผู้ป่วย',    icon: CalendarDays },
  { to: 'treatments',   label: 'การรักษา',     icon: Stethoscope },
  { to: 'chart',        label: 'Chart',        icon: ClipboardList },
  { to: 'promotion',    label: 'โปรโมชั่น',      icon: BadgePercent },
  { to: 'lab',          label: 'Lab',          icon: FlaskConical },
  { to: 'follow-ups',   label: 'ติดตาม',       icon: Pin },
  { to: 'log',          label: 'Log',          icon: ScrollText },
]

export default function PatientLayout() {
  const { id } = useParams()
  const location = useLocation()
  const me = useMe()
  // Same identity card as the main sidebar — same name source (lib/me.js).
  const displayName = useDisplayName()
  const [patient, setPatient] = useState(null)
  const [error, setError] = useState(null)
  // Last realtime event for this patient + the counter that refetches the row.
  const [live, setLive] = useState(null)
  const [patientTick, setPatientTick] = useState(0)
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('ss_sidebar_collapsed') === '1' } catch { return false }
  })
  const [mobileNav, setMobileNav] = useState(false)
  // ข้อความ dock. `?chat=1` is how the toast and the sidebar inbox deep-link
  // straight into the conversation — landing on the patient with the message
  // already on screen, rather than one more click away.
  const [chatOpen, setChatOpen] = useState(() => {
    try { return new URLSearchParams(location.search).get('chat') === '1' } catch { return false }
  })

  // The patient top bar is sticky; its height varies (alert chips can wrap), so
  // we measure it into --ph. Sub-pages (e.g. PatientInfo's section nav) offset
  // their own sticky elements from this so nothing overlaps.
  const headerRef = useRef(null)
  // Recomputed only when the note array itself changes, so the alert's `notes`
  // prop keeps its identity across unrelated re-renders of the layout.
  const pinnedNotes = useMemo(() => (patient ? popupNotes(patient) : []), [patient])
  useEffect(() => {
    const el = headerRef.current
    if (!el) return
    const apply = () => document.documentElement.style.setProperty('--ph', `${el.offsetHeight}px`)
    apply()
    const ro = new ResizeObserver(apply)
    ro.observe(el)
    return () => ro.disconnect()
  }, [patient, error])

  useEffect(() => {
    try { localStorage.setItem('ss_sidebar_collapsed', collapsed ? '1' : '0') } catch { /* ignore */ }
  }, [collapsed])

  // While this page's dock is open, ChatToaster suppresses toasts for it — the
  // thread is already on screen and updating live.
  useEffect(() => {
    if (!id || !chatOpen) return
    markChatDockOpen(id, true)
    return () => markChatDockOpen(id, false)
  }, [id, chatOpen])

  // A toast or inbox click about THIS patient opens the dock the page already
  // owns, rather than ChatDockHost stacking a second copy of the same thread.
  useEffect(() => onOpenChatDock((r) => {
    if (String(r.patientId) === String(id)) setChatOpen(true)
  }), [id])

  // Close the mobile drawer on navigation.
  useEffect(() => { setMobileNav(false) }, [location.pathname])

  // Lock body scroll while the mobile drawer is open.
  useEffect(() => {
    if (!mobileNav) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [mobileNav])

  // Blank the row only when the PATIENT changes. A realtime refetch must not clear
  // it — every tab reads `patient` off the context, and a null would flash the whole
  // page back to its loading state.
  useEffect(() => {
    setPatient(null)
    setError(null)
  }, [id])

  useEffect(() => {
    let alive = true
    apiGet(`/api/patients/${id}`)
      .then((p) => { if (alive) setPatient(p) })
      .catch((e) => { if (alive) setError(e?.message || 'โหลดข้อมูลไม่สำเร็จ') })
    return () => { alive = false }
  }, [id, patientTick])

  // Browser tab title: the ten patient tabs are indistinguishable in a tab strip
  // otherwise. Restored on unmount so the rest of the CRM keeps the static title.
  useEffect(() => {
    if (!patient) return
    const hn = patient.hn || patient.HN || ''
    // Name only — no prefix, no nickname; a tab strip has no room for them.
    const name = [patient.name, patient.surname].filter(Boolean).join(' ').trim()
      || patient.fullname || ''
    document.title = `SSDC - ${[name, hn].filter(Boolean).join(' ') || 'Patient'}`
    return () => { document.title = 'Smile Seasons CRM' }
  }, [patient])

  // THE ONE realtime subscription for the whole patient page. Xano table triggers
  // (`patient_update - *`) broadcast every row change on this patient's ten tables
  // to `patient_update/{id}`; the layout re-broadcasts the event to the tabs through
  // the outlet context (`live`) so no sub-page opens a channel of its own.
  //
  // Skipping our own writes needs BOTH ids present: the patient and chart triggers
  // cannot name an actor, so those events come back to the writer too.
  useXanoRealtime(id ? `patient_update/${id}` : null, (msg) => {
    const p = msg?.payload?.data ?? msg?.payload
    if (!p || typeof p !== 'object') return
    if (String(p.patient_id) !== String(id)) return
    // 🚨 The type is `chat_message`, NOT `chat` — one letter from `chart`, which
    // is what makes the chart detail raise its "แก้ไขจากเครื่องอื่น · โหลดใหม่" pill.
    // A typo either way would tell a doctor mid-note that their open chart is
    // stale on every message sent. Owner's rule: chat never disturbs a chart.
    //
    // It is also the one type keyed on `user_id` rather than `employee_id`: a
    // doctor-only session has employee_id 0, so with the employee test two
    // different doctors would silently drop each other's messages as own-echo.
    if (p.type === 'chat_message') {
      if (p.user_id != null && me?.xano_id != null && String(p.user_id) === String(me.xano_id)) return
    } else if (p.employee_id != null && p.employee_id !== '' && me?.employee_id != null
        && String(p.employee_id) === String(me.employee_id)) return
    // `at` makes every event a distinct object, so two identical ones still re-render.
    setLive({ ...p, at: Date.now() })
    // Trigger 2 (an appointment write moved the status) sends the NEW value with
    // `action: "status"`. Patch the pill now instead of trusting the refetch below:
    // trigger 19's `appointment` event for the same write lands within milliseconds,
    // in either order (the triggers run concurrently), and only the last `live`
    // value survives a render. Trigger 28's generic patient event carries no
    // status and still goes through the refetch.
    if (p.type === 'patient' && p.action === 'status' && typeof p.status === 'string') {
      setPatient((prev) => (prev ? { ...prev, status: p.status } : prev))
    }
  })

  // The header (name, สถานะ, allergy/bleeding chips, popup notes) and the ข้อมูล
  // tab both read the patient row, so the layout refetches it itself. Debounced:
  // one appointment write can rewrite the patient row three times over via the
  // last_appointment / status / high_billing cascade triggers.
  //
  // 🚨 No per-event cleanup. It used to clear the timer every time `live` changed,
  // so an `appointment` event arriving inside the 500 ms window CANCELLED the
  // pending patient refetch and the header silently kept the old status.
  const patientTimer = useRef()
  useEffect(() => {
    if (!live || (live.type !== 'patient' && live.type !== 'recall')) return
    clearTimeout(patientTimer.current)
    patientTimer.current = setTimeout(() => setPatientTick((t) => t + 1), 500)
  }, [live])
  useEffect(() => () => clearTimeout(patientTimer.current), [])

  // Doctor-only session (SSO from doctor_portal): Chart is the only menu, and
  // patient contact info is hidden. Employees keep the full menu.
  const doctorOnly = isDoctorOnly(me)
  const nav = doctorOnly ? NAV.filter((n) => n.to === 'chart') : NAV

  // Confine doctor-only sessions to Chart — bounce any other patient sub-route
  // (info/appointments/profile/…) to the chart once `me` is resolved.
  const onChart = /^\/patient\/[^/]+\/chart(\/|$)/.test(location.pathname)
  // The visit in view, if any — a message sent from a chart detail carries it
  // so the bubble can say WHICH visit it was about. Xano validates it against
  // the patient and drops it if it belongs to someone else.
  const treatmentId = Number(/^\/patient\/[^/]+\/chart\/history\/(\d+)/.exec(location.pathname)?.[1]) || 0
  if (doctorOnly && !onChart) {
    return <Navigate to={`/patient/${id}/chart/history`} replace />
  }

  return (
    <div className={`flex h-full min-h-screen ${chatOpen ? 'xl:pr-[400px]' : ''}`}>
      {/* Desktop sidebar */}
      <aside
        className={`${collapsed ? 'w-[68px]' : 'w-[220px]'} hidden lg:flex flex-none bg-white border-r border-slate-200/70 flex-col transition-[width] duration-200`}
      >
        <SidebarInner
          id={id}
          me={me}
          displayName={displayName}
          patient={patient}
          nav={nav}
          doctorOnly={doctorOnly}
          collapsed={collapsed}
          showCollapse
          onToggleCollapse={() => setCollapsed((v) => !v)}
        />
      </aside>

      {/* Mobile drawer + backdrop */}
      <div
        className={`lg:hidden fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-[1px] transition-opacity duration-200 ${
          mobileNav ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setMobileNav(false)}
        aria-hidden
      />
      <aside
        className={`lg:hidden fixed z-50 inset-y-0 left-0 w-[272px] max-w-[82vw] bg-white border-r border-slate-200 flex flex-col shadow-2xl transition-transform duration-200 ${
          mobileNav ? 'translate-x-0' : '-translate-x-full'
        }`}
        aria-hidden={!mobileNav}
      >
        <SidebarInner
          id={id}
          me={me}
          displayName={displayName}
          patient={patient}
          nav={nav}
          doctorOnly={doctorOnly}
          collapsed={false}
          onNavigate={() => setMobileNav(false)}
          onClose={() => setMobileNav(false)}
        />
      </aside>

      {/* Content */}
      <main className="flex-1 min-w-0 overflow-auto">
        {/* Mobile top app bar */}
        <div className="lg:hidden sticky top-0 z-30 flex items-center gap-2.5 px-3.5 h-14 bg-white/90 backdrop-blur supports-[backdrop-filter]:bg-white/75 border-b border-slate-200/70">
          <button
            type="button"
            onClick={() => setMobileNav(true)}
            aria-label="เปิดเมนู"
            className="w-9 h-9 -ml-1 flex items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100 transition-colors"
          >
            <Menu size={20} strokeWidth={2} />
          </button>
          <BrandMark size={32} />
          <div className="min-w-0 flex-1">
            <div className="text-[14px] font-semibold text-slate-900 truncate leading-tight">
              {patient ? formatName(patient) : 'Patient'}
            </div>
            {patient && (
              <div className="text-[12px] text-slate-400 font-mono leading-tight">HN {patient.hn || patient.HN || '—'}</div>
            )}
          </div>
          <div className="w-9 flex-none">
            {!doctorOnly && <SearchTrigger collapsed />}
          </div>
        </div>

        <div ref={headerRef} className="sticky top-14 lg:top-0 z-20">
          <PatientHeader
            patient={patient}
            error={error}
            hideContact={doctorOnly}
            onStatusChange={(s) => setPatient((p) => (p ? { ...p, status: s } : p))}
            chatButton={
              <PatientChatButton
                patientId={id}
                open={chatOpen}
                onToggle={() => setChatOpen((v) => !v)}
              />
            }
          />
        </div>
        <div className="px-4 sm:px-6 lg:px-10 py-5 lg:py-7">
          <Outlet context={{ patient, setPatient, live }} />
        </div>
      </main>

      {/* Fires ONCE per session per patient, and again whenever a popup note
          changes — see PatientNoteAlert for why it is not on every open. It sits
          in the LAYOUT, not in PatientHeader, for two reasons:
            · this component is shared by every patient tab, so entering the
              patient interrupts once rather than once per tab;
            · the header lives inside `sticky … z-20`, which is a STACKING
              CONTEXT — a z-[60] overlay nested in it still cannot rise above the
              z-50 drawers rendered elsewhere on the page, and the alert would
              have been painted underneath them.
          `key` forces a remount per patient so the acknowledgement is re-read
          for the right one instead of carrying over for a frame. */}
      {/* ข้อความ — see PatientChatDock for why it is a dock, why the trigger is in
          the header, and why both are mounted from here rather than nested in it. */}
      <PatientChatDock
        patientId={id}
        patientName={patient ? formatName(patient) : ''}
        treatmentId={treatmentId}
        me={me}
        live={live}
        open={chatOpen}
        onClose={() => setChatOpen(false)}
      />

      {/* The reach half: a message about ANOTHER patient still has to find you. */}
      <ChatToaster />
      {/* Requests for ANOTHER patient open here; one for the patient this page
          already owns a dock for is handled above instead, so the same thread
          never stacks on top of itself. */}
      <ChatDockHost skipPatientId={id} />

      {pinnedNotes.length > 0 && (
        <PatientNoteAlert
          key={patient.id}
          patientId={patient.id}
          patientName={formatName(patient)}
          notes={pinnedNotes}
          me={me}
          setPatient={setPatient}
        />
      )}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Sidebar contents — shared by the desktop rail and the mobile drawer        */
/* -------------------------------------------------------------------------- */

// `displayName` is threaded in from PatientLayout rather than read here: it is the
// same value the outer shell already resolved, and calling the hook again would
// subscribe a second time to render the identical string.
function SidebarInner({ id, me, displayName, patient, nav = NAV, doctorOnly = false, collapsed, onNavigate, showCollapse, onToggleCollapse, onClose }) {
  return (
    <>
      {/* Brand */}
      <div className="px-4 pt-5 pb-4 flex items-center gap-2.5">
        <BrandMark size={36} />
        {!collapsed && (
          <div className="min-w-0">
            <div className="text-[14.5px] font-semibold text-slate-900 leading-tight">Patient</div>
            <div className="text-[12px] uppercase tracking-[0.14em] text-slate-400 mt-0.5">รายผู้ป่วย</div>
          </div>
        )}
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="ปิดเมนู"
            className="ml-auto w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
          >
            <X size={16} strokeWidth={2.25} />
          </button>
        )}
      </div>

      {/* Pinned patient-search trigger — hidden for doctor-only sessions */}
      {!doctorOnly && (
        <div className="px-3 pb-3">
          <SearchTrigger collapsed={collapsed} />
        </div>
      )}

      {/* Patient mini-card */}
      {!collapsed && (
        <div className="mx-3 mb-3 px-3 py-2.5 rounded-lg bg-slate-50 border border-slate-100">
          {patient ? (
            <>
              <div className="text-[14px] font-semibold text-slate-900 truncate">{formatName(patient)}</div>
              <div className="text-[12.5px] text-slate-500 font-mono mt-0.5">HN {patient.hn || patient.HN || '—'}</div>
            </>
          ) : (
            <div className="h-8 bg-slate-200/50 rounded animate-pulse" />
          )}
        </div>
      )}

      {!collapsed && (
        <div className="px-4 pb-2 text-[12px] font-medium uppercase tracking-[0.12em] text-slate-400">เมนู</div>
      )}

      <nav className="flex-1 px-2 space-y-0.5 overflow-y-auto">
        {nav.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={`/patient/${id}/${to}`}
            onClick={onNavigate}
            title={collapsed ? label : undefined}
            className={({ isActive }) =>
              `group flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[14px] font-medium transition-colors ${
                isActive ? 'bg-brand-50 text-brand-700' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
              } ${collapsed ? 'justify-center' : ''}`
            }
          >
            <Icon size={16} className="flex-none" strokeWidth={1.75} />
            {!collapsed && <span className="truncate">{label}</span>}
          </NavLink>
        ))}
      </nav>

      <div className="px-2 pb-3 pt-2 border-t border-slate-100 space-y-1">
        {!collapsed && me && (
          <div className="px-2 py-2 rounded-lg flex items-center gap-2.5 hover:bg-slate-50 transition-colors">
            <div className="w-8 h-8 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center overflow-hidden flex-none ring-1 ring-slate-200/70">
              {me.image
                ? <img src={me.image} alt="" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                : <span className="text-[14px] font-semibold">{nameInitial(displayName || me.email)}</span>}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[14px] font-semibold text-slate-900 truncate">{displayName || '—'}</div>
              <div className="text-[12.5px] text-slate-500 truncate">{me.email}</div>
            </div>
            <a
              href="/__app/logout"
              title="ออกจากระบบ"
              onClick={() => clearAllRecentPatients()}
              className="p-1.5 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
            >
              <LogOut size={14} strokeWidth={2} />
            </a>
          </div>
        )}
        {!doctorOnly && (
          <Link
            to="/"
            onClick={onNavigate}
            title={collapsed ? 'กลับหน้าหลัก' : undefined}
            className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-[13.5px] text-slate-500 hover:text-brand-700 hover:bg-brand-50 transition-colors ${
              collapsed ? 'justify-center' : ''
            }`}
          >
            <ArrowLeft size={13} strokeWidth={2} />
            {!collapsed && <span>กลับหน้าหลัก</span>}
          </Link>
        )}
        {showCollapse && (
          <button
            onClick={onToggleCollapse}
            className="mt-1 w-full flex items-center justify-center gap-1.5 text-[12.5px] text-slate-400 hover:text-slate-600 py-1.5 rounded-md hover:bg-slate-50 transition-colors"
            title={collapsed ? 'ขยาย' : 'ย่อ'}
          >
            {collapsed ? <PanelLeftOpen size={14} /> : <><PanelLeftClose size={14} /><span>ย่อแถบเมนู</span></>}
          </button>
        )}
      </div>
    </>
  )
}

function formatName(p) {
  if (!p) return '—'
  const full = [p.prefix, p.name, p.surname].filter(Boolean).join(' ').trim() || p.fullname || '—'
  const nick = p.nickname ? ` (${p.nickname})` : ''
  return `${full}${nick}`
}

function calcAge(p) {
  if (!p) return null
  if (p.age != null) return p.age
  const dob = p.dob || p.birth_date || p.birthday
  if (!dob) return null
  const d = new Date(dob)
  if (isNaN(d)) return null
  const diff = Date.now() - d.getTime()
  return Math.floor(diff / (365.25 * 24 * 3600 * 1000))
}

// Critical medical flags shown on EVERY patient subpage: drug/food allergies
// and bleeding-risk medications. Both matter before any dental procedure.
function medicalFlags(patient) {
  const clean = (v) => String(v ?? '').trim()
  const fromList = (list, other) => {
    const items = [
      ...(Array.isArray(list) ? list : []),
      ...clean(other).split(/[,;\n]/),
    ].map(clean).filter((s) => s && s !== '-')
    return [...new Set(items)]
  }
  return {
    allergy: fromList(patient?.allergy, patient?.allergy_other),
    disease: fromList(patient?.disease, patient?.disease_other),
    bleeding: fromList(patient?.bleeding_med, ''),
  }
}

// Compact, icon-led alert chip for the patient top bar. The icon stands in for
// the label (⚠ = allergy, 💧 = bleeding-risk meds); the values follow, truncated.
// These three flags are safety-critical before any procedure, so the truncated
// text is never the only way to read them — the chip is a button that opens the
// full, unabbreviated list, and a count shows when there is more than one item.
function AlertChip({ tone, icon: Icon, items, title }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    if (!open) return
    const onDown = (e) => { if (!ref.current?.contains(e.target)) setOpen(false) }
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const tones = {
    rose:  { chip: 'bg-rose-50 text-rose-700 ring-rose-200 hover:bg-rose-100',    head: 'text-rose-700',  dot: 'bg-rose-400',  count: 'bg-rose-200/70 text-rose-800' },
    amber: { chip: 'bg-amber-50 text-amber-800 ring-amber-200 hover:bg-amber-100', head: 'text-amber-800', dot: 'bg-amber-400', count: 'bg-amber-200/70 text-amber-900' },
    sky:   { chip: 'bg-sky-50 text-sky-700 ring-sky-200 hover:bg-sky-100',        head: 'text-sky-700',   dot: 'bg-sky-400',   count: 'bg-sky-200/70 text-sky-800' },
    // Fallback only — an unknown tone must degrade, not white-screen every patient page.
    slate: { chip: 'bg-slate-50 text-slate-700 ring-slate-200 hover:bg-slate-100', head: 'text-slate-700', dot: 'bg-slate-400', count: 'bg-slate-200/70 text-slate-800' },
  }
  const t = tones[tone] || tones.slate

  return (
    <span ref={ref} className="relative inline-flex">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        title={`${title}: ${items.join(', ')}`}
        className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-[13.5px] font-semibold ring-1 transition-colors ${t.chip}`}
      >
        <Icon size={13} strokeWidth={2.5} className="flex-none" />
        <span className="truncate max-w-[120px] sm:max-w-[200px]">{items.join(', ')}</span>
        {items.length > 1 && (
          <span className={`flex-none px-1 rounded text-[11px] font-bold tabular-nums ${t.count}`}>{items.length}</span>
        )}
      </button>

      {open && (
        <div className="absolute z-40 top-full left-0 sm:left-1/2 sm:-translate-x-1/2 mt-1.5 w-max min-w-[200px] max-w-[min(340px,calc(100vw-2rem))] rounded-xl bg-white ring-1 ring-slate-200 shadow-lg p-3 text-left">
          <div className={`text-[12px] font-bold uppercase tracking-wide mb-1.5 flex items-center gap-1.5 ${t.head}`}>
            <Icon size={12} strokeWidth={2.5} />
            {title}
          </div>
          <ul className="space-y-1">
            {items.map((it, i) => (
              <li key={i} className="flex items-start gap-2 text-[13.5px] text-slate-700 leading-snug">
                <span className={`mt-[6px] w-1.5 h-1.5 rounded-full flex-none ${t.dot}`} />
                <span className="break-words">{it}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </span>
  )
}

// Pinned patient notes (`patient.note[].popup`). Its own component rather than a
// fourth AlertChip because the content is different in kind: sampled live, two of
// three popup notes are multi-line running deposit ledgers kept over years, so the
// popover renders `details` with whitespace-pre-wrap, unabbreviated, one block per
// note — never comma-joined into a truncated single line.
function NoteChip({ notes }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    if (!open) return
    const onDown = (e) => { if (!ref.current?.contains(e.target)) setOpen(false) }
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  // One line of preview only — the full text lives in the popover.
  const preview = String(notes[0]?.details || '').split('\n').map((l) => l.trim()).find(Boolean) || 'โน้ต'

  return (
    <span ref={ref} className="relative inline-flex">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        title="โน้ตที่ปักหมุด"
        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-[13.5px] font-semibold ring-1 bg-amber-50/70 text-slate-700 ring-amber-300 hover:bg-amber-100 transition-colors"
      >
        <Pin size={13} strokeWidth={2.5} className="flex-none text-amber-600" />
        <span className="truncate max-w-[120px] sm:max-w-[200px]">{preview}</span>
        {notes.length > 1 && (
          <span className="flex-none px-1 rounded text-[11px] font-bold tabular-nums bg-amber-200/70 text-amber-900">{notes.length}</span>
        )}
      </button>

      {open && (
        <div className="absolute z-40 top-full left-0 sm:left-1/2 sm:-translate-x-1/2 mt-1.5 w-max min-w-[240px] max-w-[min(420px,calc(100vw-2rem))] rounded-xl bg-white ring-1 ring-slate-200 shadow-lg p-3 text-left">
          <div className="text-[12px] font-bold uppercase tracking-wide mb-2 flex items-center gap-1.5 text-amber-700">
            <Pin size={12} strokeWidth={2.5} /> โน้ตที่ปักหมุด
          </div>
          <div className="space-y-2.5 max-h-[50vh] overflow-y-auto">
            {notes.map((n, i) => (
              <div key={n.unique_id || i} className="text-[13.5px] text-slate-700 leading-relaxed">
                {/* pre-wrap is the readability of this field, not styling. */}
                <p className="whitespace-pre-wrap break-words">{n.details}</p>
                {n.code && <p className="mt-0.5 text-[11.5px] text-slate-400">— {n.code}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
    </span>
  )
}

const THAI_MONTHS_SHORT = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.']

// Birthday proximity for a friendly staff greeting cue. Tiered so it's loud on
// the day, clear inside the next week, faint out to 30 days ahead, plus a
// belated marker for the week just gone — invisible otherwise.
// The window is a ROLLING 30 days, not the calendar month it used to be: "same
// month" both hid a birthday four days away that happened to land on the 1st of
// the next one, and marked one that passed three weeks ago.
// Parsed as a local date (no timezone drift).
function birthdayInfo(birthday) {
  const m = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(String(birthday || ''))
  if (!m) return null
  const by = +m[1], bmo = +m[2] - 1, bdd = +m[3]
  if (bmo < 0 || bmo > 11 || bdd < 1 || bdd > 31) return null

  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const MS = 86400000
  // Nearest occurrence among last/this/next year.
  let best = null
  for (const y of [today.getFullYear() - 1, today.getFullYear(), today.getFullYear() + 1]) {
    const date = new Date(y, bmo, bdd)
    const diff = Math.round((date - today) / MS)
    if (!best || Math.abs(diff) < Math.abs(best.diff)) best = { diff, date }
  }
  const days = best.diff // 0 = today, + upcoming, − just passed
  const turning = best.date.getFullYear() - by
  const dateLabel = `${bdd} ${THAI_MONTHS_SHORT[bmo]}`

  let tier = null
  if (days === 0) tier = 'today'
  else if (days >= 1 && days <= 7) tier = 'soon'
  else if (days >= 8 && days <= 30) tier = 'near'
  else if (days <= -1 && days >= -7) tier = 'recent'
  return { tier, days, turning, dateLabel }
}

function BirthdayChip({ info }) {
  if (!info || !info.tier) return null
  const title = `วันเกิด ${info.dateLabel}${info.turning ? ` · ครบ ${info.turning} ปี` : ''}`
  const base = 'inline-flex items-center gap-1 rounded-full font-medium ring-1'
  if (info.tier === 'today') {
    return <span title={title} className={`${base} px-2 py-0.5 text-[13px] font-semibold bg-pink-100 text-pink-700 ring-pink-200`}><Cake size={12} strokeWidth={2.25} /> วันเกิดวันนี้!</span>
  }
  if (info.tier === 'soon') {
    return <span title={title} className={`${base} px-2 py-0.5 text-[13px] bg-pink-50 text-pink-600 ring-pink-200`}><Cake size={12} strokeWidth={2.25} /> อีก {info.days} วัน</span>
  }
  if (info.tier === 'recent') {
    return <span title={title} className={`${base} px-2 py-0.5 text-[13px] bg-pink-50 text-pink-500 ring-pink-100`}><Cake size={12} strokeWidth={2.25} /> เมื่อ {Math.abs(info.days)} วันก่อน</span>
  }
  // 'near' — 8–30 days out. Still spelled out in words rather than left to the
  // avatar ring alone, so the cue does not depend on telling two pinks apart.
  return <span title={title} className={`${base} px-2 py-0.5 text-[13px] bg-white text-pink-500 ring-pink-100`}><Cake size={12} strokeWidth={2} /> อีก {info.days} วัน</span>
}

// The avatar ring for the same window — the at-a-glance half of the cue, read
// before anyone gets to the chip. Nothing for 'recent': a ring says "coming up",
// and a belated birthday is the chip's job. No key → the avatar keeps its
// default brand ring.
const BIRTHDAY_RING = {
  today: 'ring-[3px] ring-pink-500 ring-offset-[3px] ring-offset-white shadow-[0_0_0_7px_rgba(236,72,153,0.10)]',
  soon:  'ring-2 ring-pink-400 ring-offset-2 ring-offset-white',
  near:  'ring-2 ring-pink-200 ring-offset-2 ring-offset-white',
}

function PatientHeader({ patient, error, hideContact = false, onStatusChange, chatButton = null }) {
  if (error) {
    return (
      <div className="bg-rose-50 border-b border-rose-200 px-4 sm:px-6 lg:px-10 py-4 text-[14px] text-rose-700">
        โหลดข้อมูลผู้ป่วยไม่สำเร็จ: {error}
      </div>
    )
  }
  if (!patient) {
    return (
      <div className="bg-white border-b border-slate-200/70 px-4 sm:px-6 lg:px-10 py-5 lg:py-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-slate-100 animate-pulse flex-none" />
          <div className="flex-1 space-y-2">
            <div className="h-5 bg-slate-100 rounded animate-pulse max-w-xs" />
            <div className="h-3.5 bg-slate-100 rounded animate-pulse max-w-md" />
          </div>
        </div>
      </div>
    )
  }
  const age = calcAge(patient)
  // Doctor-only sessions never see patient contact info.
  const phone = hideContact ? null : (patient.contact?.phone || patient.phone || patient.tel)
  const { allergy, disease, bleeding } = medicalFlags(patient)
  const pins       = popupNotes(patient)
  const bday       = birthdayInfo(patient.birthday || patient.dob || patient.birth_date)
  const bdayRing   = bday?.tier ? BIRTHDAY_RING[bday.tier] : null
  const bdayTitle  = bday?.tier ? `วันเกิด ${bday.dateLabel}${bday.turning ? ` · ครบ ${bday.turning} ปี` : ''}` : null
  const status     = patient.status || patient.appointment_status

  return (
    <div className="bg-white border-b border-slate-200/70 px-4 sm:px-6 lg:px-10 py-4 sm:py-5 lg:py-6">
      <div className="flex items-start gap-4 sm:gap-6 flex-wrap">
        <div className="flex items-center gap-3 sm:gap-4 min-w-0">
          {/* Inside the 30-day birthday window the avatar wears a pink ring, and on
              the day itself a cake badge. The badge is a SIBLING of the image box,
              not a child: that box is overflow-hidden and would clip it away. */}
          <div className="relative flex-none" title={bdayTitle || undefined}>
            <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-brand-50 text-brand-700 flex items-center justify-center overflow-hidden ${bdayRing || 'ring-1 ring-brand-100'}`}>
              {patient.line_picture_url ? (
                <img
                  src={patient.line_picture_url}
                  alt=""
                  className="w-full h-full object-cover"
                  referrerPolicy="no-referrer"
                  onError={(e) => { e.currentTarget.style.display = 'none' }}
                />
              ) : (
                <User size={20} strokeWidth={1.75} />
              )}
            </div>
            {bday?.tier === 'today' && (
              <span className="absolute -bottom-0.5 -right-0.5 w-[18px] h-[18px] sm:w-5 sm:h-5 rounded-full bg-pink-500 text-white ring-2 ring-white flex items-center justify-center">
                <Cake size={11} strokeWidth={2.5} />
              </span>
            )}
          </div>
          <div className="min-w-0">
            <div className="text-[17px] sm:text-[20px] font-semibold text-slate-900 tracking-tight truncate">
              {formatName(patient)}
            </div>
            <div className="text-[13.5px] sm:text-[14px] text-slate-500 mt-1 flex items-center gap-2.5 flex-wrap">
              {age != null && <span>{age} ปี</span>}
              {bday?.tier && <BirthdayChip info={bday} />}
              {age != null && <span className="text-slate-300">·</span>}
              <span className="font-mono">HN {patient.hn || patient.HN || '—'}</span>
              {phone && <span className="text-slate-300">·</span>}
              {phone && <span className="inline-flex items-center gap-1"><Phone size={11.5} className="text-slate-400" />{phone}</span>}
              <ScaleTag src={patient} />
            </div>
          </div>
        </div>

        {(allergy.length > 0 || disease.length > 0 || bleeding.length > 0 || pins.length > 0) && (
          <div className="flex items-center gap-2 flex-wrap">
            {allergy.length > 0 && <AlertChip tone="rose" icon={AlertTriangle} items={allergy} title="แพ้ยา/อาหาร" />}
            {disease.length > 0 && <AlertChip tone="sky" icon={HeartPulse} items={disease} title="โรคประจำตัว" />}
            {bleeding.length > 0 && <AlertChip tone="amber" icon={Droplet} items={bleeding} title="ยาเสี่ยงเลือดออก" />}
            {/* Pinned notes sit AFTER the clinical chips and behind a divider. They are a
                different KIND of thing — the live ones are service warnings and running
                deposit ledgers, not safety flags — and the row above is described in this
                file as "safety-critical before any procedure". Mixing the two teaches
                staff the row is a mixed bag, which is how a real allergy chip stops being
                read. Deliberately NOT an <AlertChip>: that one comma-joins its items and
                truncates to 200px, which turns a multi-line balance sheet into gibberish. */}
            {pins.length > 0 && (allergy.length > 0 || disease.length > 0 || bleeding.length > 0) && (
              <span className="w-px h-5 bg-slate-200 shrink-0" aria-hidden />
            )}
            {pins.length > 0 && <NoteChip notes={pins} />}
          </div>
        )}

        <div className="sm:ml-auto flex items-center gap-2">
          {chatButton}
          <StatusControl patientId={patient.id} status={status} onChanged={onStatusChange} disabled={hideContact} align="right" />
        </div>
      </div>
    </div>
  )
}

